import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';
import AuthTop from 'components/AuthTop';
import Input from 'components/Input';
import Validators, { phoneNumberParse } from 'utils/fieldValidators';
import {
  COUNTRIES,
  COUNTRY_PHONE_EXAMPLE,
  COUNTRY_STATES
} from 'utils/geographicData';
import RewardsCardsIcon from 'resources/rewards-cards.png';
import { FORM_NAME, SubmitStatus } from '../formConstants';
import './CreateCardFormPage.css';

const countryOptions = COUNTRIES.map(c => ({ value: c, label: c }));
const countryStateOptions = Object.keys(COUNTRY_STATES).reduce(function (
  result,
  key
) {
  const states = COUNTRY_STATES[key];
  result[key] = states.map(s => ({ value: s, label: s }));
  return result;
},
{});

class CreateCardFormPage extends Component {
  render() {
    const { formRenderer, country } = this.props;
    const countryStates = country && countryStateOptions[country];
    const phonePlaceholder = country
      ? COUNTRY_PHONE_EXAMPLE[country] || '+XXXXXXXXXXXX'
      : '';

    const body = (
      <AuthTop hideLogo>
        <div className="create-card-form-page">
          <div className="form-title">
            <img
              className="rewards-cards-image"
              src={RewardsCardsIcon}
              alt="Rewards Cards"
            />
            <div>Rewards Card Details</div>
          </div>
          <h2 className="sr-only focus-start" tabIndex="-1">
            New Rewards Card Details
          </h2>
          <div className="form-page create-card-form-page">
            <div className="form-panel">
              <div className="form-field">
                <Field
                  name="firstName"
                  component={Input}
                  type="text"
                  label="First Name*"
                  autoComplete="given-name"
                  disableOnSubmitting={true}
                  validate={Validators.trimmedRequired}
                  required
                />
                <Field
                  name="lastName"
                  component={Input}
                  type="text"
                  label="Last Name*"
                  autoComplete="family-name"
                  disableOnSubmitting={true}
                  validate={Validators.trimmedRequired}
                  required
                />
                <Field
                  name="address"
                  component={Input}
                  type="text"
                  label="Address*"
                  autoComplete="address-line1"
                  disableOnSubmitting={true}
                  validate={Validators.trimmedRequired}
                  required
                />
                <Field
                  name="city"
                  component={Input}
                  type="text"
                  label="City*"
                  autoComplete="address-level2"
                  disableOnSubmitting={true}
                  validate={Validators.required}
                  required
                />
                <Field
                  name="country"
                  component={Input}
                  type="select"
                  label="Country*"
                  autoComplete="country-name"
                  disableOnSubmitting={true}
                  validate={Validators.required}
                  required
                >
                  {countryOptions}
                </Field>
                {countryStates && (
                  <Field
                    key={country}
                    name="state"
                    component={Input}
                    type="select"
                    label={countryStates ? 'State/Province*' : 'State/Province'}
                    autoComplete="address-level1"
                    disableOnSubmitting={true}
                    validate={countryStates && Validators.required}
                    required={!!countryStates}
                  >
                    {countryStates}
                  </Field>
                )}
                <Field
                  name="zipCode"
                  component={Input}
                  type="text"
                  label={country === 'USA' ? 'Postal Code*' : 'Postal Code'}
                  autoComplete="postal-code"
                  disableOnSubmitting={true}
                  validate={Validators.zipCode}
                  required={country === 'USA'}
                />
                <Field
                  name="mobilePhone"
                  component={Input}
                  type="text"
                  label="Phone Number*"
                  autoComplete="mobile tel-national"
                  inputMode="numeric"
                  disableOnSubmitting={true}
                  placeholder={phonePlaceholder}
                  parse={phoneNumberParse}
                  validate={Validators.phoneNumber}
                  required
                />
                <Field
                  name="eventCode"
                  component={Input}
                  type="text"
                  label="Event Code"
                  autoComplete="off"
                  disableOnSubmitting={true}
                  tooltip="If you are signing up as part of a promotional event, enter the code here"
                />
              </div>
              <div className="form-button">
                <button
                  className={`btn button-color-green btn-block`}
                  disabled={this.props.submitting}
                  type="submit"
                >
                  {this.props.submitText}
                  <SubmitStatus submitting={this.props.submitting} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </AuthTop>
    );

    return formRenderer(this.props, body);
  }
}

const formSelector = formValueSelector(FORM_NAME);
const mapStateToProps = createStructuredSelector({
  country: state => formSelector(state, 'country')
});
const withConnect = connect(mapStateToProps);

const withReduxForm = reduxForm({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true
});

const WrappedComponent = withConnect(withReduxForm(CreateCardFormPage));

WrappedComponent.FormFields = [
  'firstName',
  'lastName',
  'address',
  'city',
  'country',
  'state',
  'zipCode',
  'mobilePhone',
  'eventCode'
];

export default WrappedComponent;
